//
// Copyright (c) 2011-2019 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';
import React, { useRef } from 'react';

import { IContentLibraryItem } from '@apis/content-library/contentLibraryApiModel';
import {
  getRejectionReasons,
  rejectModerationMedia,
} from '@apis/moderation/moderationApi';
import VirtualizedContentCardGrid from '@components/organisms/content-card-grid/VirtualizedContentCardGrid';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';
import Cuid from '@models/Cuid';
import { RejectionCode } from '@models/RejectionCode';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';
import { getClientId } from '@redux/selectors/getClientId';
import { getIsManualModerationRejectEnabled } from '@redux/selectors/getIsManualModerationRejectEnabled';
import * as shareApi from '@share/api/shareApi';
import StreamsDrawerGridMediaCard from '@streams-drawer/streams-drawer-content/streams-drawer-moderation-content/StreamsDrawerGridMediaCard';
import StreamsDrawerListMediaCard from '@streams-drawer/streams-drawer-content/streams-drawer-moderation-content/StreamsDrawerListMediaCard';
import StreamsDrawerRejectionReasonsModal from '@streams-drawer/streams-drawer-content/streams-drawer-moderation-content/StreamsDrawerRejectionReasonsModal';
import { StreamsDrawerStreamType } from '@streams-drawer/streams-drawer-constants';
import { actions as streamDrawerActions } from '@streams/actions/streamDrawerActions';
import styled from 'styled-components';
import { itemToPartialCuid } from '@utils/partialCuidUtils';
import { useDispatch, useSelector } from 'react-redux';

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  layout: 'list' | 'grid';
  items: IContentLibraryItem[];
  disableBulkActions: boolean;
  disableRejection: boolean;
}

const LIST_CONTENT_CARD_HEIGHT = 300;
const LIST_CONTENT_CARD_WIDTH = 712;

const GRID_CONTENT_CARD_HEIGHT = 220;
const GRID_CONTENT_CARD_WIDTH = 220;

export const StreamsDrawerModerationList = ({
  layout,
  items,
  disableBulkActions,
  disableRejection,
}: Props) => {
  const dispatch = useDispatch();
  const isManualModerationRejectEnabled = useSelector(
    getIsManualModerationRejectEnabled,
  );
  const clientId = useSelector(getClientId);

  const containerRef = useRef();
  const {
    clientRejectionReasons,
    selectedStream,
    selectedStreamType,
    isFetchingItems,
    isEndOfContentReached,
  } = useSelector((state) => state.streams.streamsDrawer);

  const usesManualModerationRejection =
    isManualModerationRejectEnabled &&
    selectedStreamType === StreamsDrawerStreamType.TAGGED_BY_CURALTE;
  const manualRejectionReasonsQuery = useQuery({
    enabled: usesManualModerationRejection,
    queryFn: ({ signal }) => getRejectionReasons({ signal }),
    queryKey: [
      QueryKeyConstants.MediaLibrary,
      QueryKeyConstants.MediaLibraryRejectionReasons,
      clientId,
    ],
    select: (response) =>
      response.data.map((reason) => ({
        rejectionCode: reason.rejectionReasonId,
        rejectionDescription: reason.description,
      })),
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
  const activeRejectionReasons = usesManualModerationRejection
    ? manualRejectionReasonsQuery.data
    : clientRejectionReasons;
  const orderedPartialCuids = items.map((item) =>
    itemToPartialCuid(item.itemType, item.id),
  );

  const rejectItem = async (cuid: Cuid, rejectionCode: number) => {
    try {
      if (usesManualModerationRejection) {
        await rejectModerationMedia({
          cuids: [cuid],
          rejectionReasonCode: RejectionCode(rejectionCode),
        });
      } else {
        await shareApi.rejectSharedMedia(cuid, rejectionCode);
      }
      void dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Success!',
          type: ToastType.SUCCESS,
          message: 'Rejected 1 item',
        }),
      );
      void dispatch(
        streamDrawerActions.removeItemInPlace(Cuid.parse(cuid).itemId),
      );
    } catch {
      void dispatch(
        toastNotificationsActions.addToastNotification({
          title: 'Error',
          type: ToastType.ERROR,
          message: 'Failed to reject item. Please try again.',
        }),
      );
    }
  };

  const cardWidth =
    layout === 'list' ? LIST_CONTENT_CARD_WIDTH : GRID_CONTENT_CARD_WIDTH;
  const cardHeight =
    layout === 'list' ? LIST_CONTENT_CARD_HEIGHT : GRID_CONTENT_CARD_HEIGHT;

  const renderCard = (item, idx) =>
    layout === 'list' ? (
      <StreamsDrawerListMediaCard
        disableRejection={disableRejection}
        item={item}
        isManualModerationRejection={usesManualModerationRejection}
        key={idx}
        rejectionReasons={activeRejectionReasons}
        rejectItem={rejectItem}
      />
    ) : (
      <StreamsDrawerGridMediaCard
        item={item}
        key={idx}
        disableBulkActions={disableBulkActions}
      />
    );

  return (
    <StreamContentCardListContainer ref={containerRef}>
      <VirtualizedContentCardGrid
        scrollContainerRef={containerRef}
        scrollContainerSelector='.streams-drawer-content-view'
        isRefreshingContent={isFetchingItems}
        postFixConfig={{
          shouldShowPostFixComponent: true,
          postFixComponent: isEndOfContentReached ? (
            <VirtualizedContentCardGrid.EndOfContent />
          ) : (
            <VirtualizedContentCardGrid.LoadingSpinner />
          ),
        }}
        cardWidth={cardWidth}
        cardHeight={cardHeight}
        numRowsFromEndBeforeCallback={2}
        onBottomReached={() =>
          dispatch(streamDrawerActions.fetchNextContentItems(selectedStream))
        }
        orderedPartialCuids={orderedPartialCuids}
      >
        {items.map(renderCard)}
      </VirtualizedContentCardGrid>
      <StreamsDrawerRejectionReasonsModal
        manualRejectionReasonsState={
          usesManualModerationRejection
            ? {
                isError: manualRejectionReasonsQuery.isError,
                isLoading: manualRejectionReasonsQuery.isLoading,
                retry: () => void manualRejectionReasonsQuery.refetch(),
              }
            : undefined
        }
        rejectionReasons={activeRejectionReasons}
        rejectItem={rejectItem}
      />
    </StreamContentCardListContainer>
  );
};

const StreamContentCardListContainer = styled.div`
  height: 100%;
  width: 100%;
`;

const PostfixComponent = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 120px;
  overflow: hidden;
`;

export default StreamsDrawerModerationList;
